import React, { useMemo, useState, useEffect } from 'react';
import { isLoading } from 'resift';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import * as FormValues from 'types/FormValue';
import * as FeatureTypes from 'pages/Features/typings';
import * as GeneralTypes from 'pages/General/typings';
import * as PageTypes from 'pages/Pages/typings';

// Components
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContentText from '@material-ui/core/DialogContentText';
import GenericLayoutBuilder from 'components/GenericLayoutBuilder';

import makeStyles from '@material-ui/styles/makeStyles';

interface Props {
  cardTitle: string;
  dialogTitle: string;
  filter: (f: any) => boolean;
  layoutKey: any;
  subtitle: string;
  userStatus: number;
  fields: FormValues.Fields;
  onChange: (field: any, value: FormValues.FormValue) => void;
  values:
    | FeatureTypes.FeatureValues
    | GeneralTypes.GeneralValues
    | PageTypes.EntitySettingsFormValues;
  typeKey: string;
  className?: string;
}

function LayoutSettingCard({
  cardTitle,
  dialogTitle,
  filter,
  layoutKey,
  subtitle,
  userStatus,
  fields,
  onChange,
  values,
  typeKey,
  className,
}: Props) {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const availableFields = useMemo(() => {
    const fieldsCollection = Object.values(fields).filter(filter);
    return _keyBy(fieldsCollection, 'objectKey');
  }, [fields]);
  const layoutValue = _get(values, ['layouts', layoutKey]);
  const layoutFields = layoutValue;
  const [layout, setLayout] = useState(layoutFields);
  function toggleDialogOpen() {
    setDialogOpen(prevDialogOpen => !prevDialogOpen);
  }
  useEffect(() => {
    setLayout(layoutValue);
  }, [values]);

  const handleCancel = () => {
    toggleDialogOpen();
    setLayout(layoutFields);
  };

  async function handleUpdateLayout() {
    onChange(`layouts.${layoutKey}`, layout);
    toggleDialogOpen();
  }

  const searchSettings = _get(values, 'settings.directorySearch');

  return (
    <Card className={className}>
      <CardHeader
        title={cardTitle}
        action={
          <Button
            data-testid="configure button"
            onClick={toggleDialogOpen}
            color="secondary"
            variant="contained"
            className={classes.button}
            // (note - pearl) Special logic for filters dialog on Page search settings
            disabled={!!searchSettings && !searchSettings.enabled}
          >
            Configure
          </Button>
        }
        subheader={subtitle}
      />
      {dialogOpen && (
        <Dialog
          open={dialogOpen}
          onClose={toggleDialogOpen}
          classes={{ paper: classes.rootDialog }}
        >
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{subtitle}</DialogContentText>
            <GenericLayoutBuilder
              allFields={Object.values(fields)}
              fields={availableFields}
              layout={layout}
              typeKey={typeKey}
              onChange={setLayout}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={isLoading(userStatus)}>
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleUpdateLayout}
              disabled={isLoading(userStatus)}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Card>
  );
}

const useStyles = makeStyles((theme: any) => {
  return {
    rootDialog: {
      minHeight: 700,
    },
    button: {
      marginTop: theme.spacing(1.5),
    },
  };
});

export default LayoutSettingCard;
